import React from 'react';
import { Alert, Col, DatePicker, Form, FormInstance, Input, InputNumber, Modal, Row, Switch } from 'antd';
import { TNumberingRange } from './types';

interface NumberingRangeFormModalProps {
  visible: boolean;
  editingRange: TNumberingRange | null;
  form: FormInstance;
  savingRange: boolean;
  onClose: () => void;
  onSaveRange: (values: any) => void;
}

export const NumberingRangeFormModal: React.FC<NumberingRangeFormModalProps> = ({
  visible,
  editingRange,
  form,
  savingRange,
  onClose,
  onSaveRange
}) => {
  return (
    <Modal
      title={editingRange ? `Editar Rango (${editingRange.prefix})` : 'Nuevo Rango de Numeración DIAN'}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={savingRange}
      destroyOnHidden
      width={620}
    >
      <Form form={form} layout="vertical" onFinish={onSaveRange}>
        {editingRange ? (
          <Alert
            type="warning"
            showIcon
            message="Resolución Registrada (Inmutable)"
            description="Por normativa de la DIAN, los datos oficiales de la resolución y su consecutivo no pueden alterarse tras su creación. Puedes modificar el estado del rango y su uso predeterminado en el sistema."
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Registro Atómico en Factus API V2"
            description="Al guardar, el rango se registrará y validará directamente ante Factus y la DIAN."
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="prefix"
              label="Prefijo DIAN"
              rules={[
                { required: true, message: 'Ingrese el prefijo (ej. SETP, FE)' },
                { max: 4, message: 'El prefijo no puede superar los 4 caracteres' }
              ]}
            >
              <Input placeholder="Ej. SETP" maxLength={4} style={{ textTransform: 'uppercase' }} disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="resolution_number"
              label="Número de Resolución DIAN"
              rules={[{ required: true, message: 'Ingrese el número de resolución DIAN' }]}
            >
              <Input placeholder="Ej. 18760000001" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="resolution_date" label="Fecha de Resolución">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Seleccione fecha" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="document_type" label="Tipo de Documento">
              <Input value="Factura de Venta (INVOICE)" disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="from_number"
              label="Número Desde"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="1" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="to_number"
              label="Número Hasta"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="10000" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="current_number"
              label="Consecutivo Actual"
              tooltip="Último número de factura emitido. Si migras un rango en uso desde otra plataforma, ingresa el último consecutivo generado (0 si es nuevo). La siguiente factura será este valor + 1."
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="valid_from" label="Vigente Desde">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Inicio vigencia" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="valid_to" label="Vigente Hasta">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Fin vigencia" disabled={Boolean(editingRange)} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="is_default" valuePropName="checked" label="Rango Predeterminado">
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="is_active" valuePropName="checked" label="Rango Activo">
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" defaultChecked />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
